import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import Button from '../Button';
import Flex from '../Flex';
import Icon, { StyledIcon, registerIcon } from '../Icon';
import * as warnIcon from '../Icon/icons/warn.icon';
import Text from '../Text';
import { useI18n } from '../../hooks';
import type { BaseProps, NoChildrenProp, ForwardRefForwardPropsComponent } from '../../types';
import { defaultThemeProp } from '../../theme';

registerIcon(warnIcon);

export interface ErrorStateProps extends BaseProps, NoChildrenProp {
  /**
   * Message to display below the icon.
   * @default 'Unknown error'
   */
  message?: string;
  /** Callback function that if provided renders a localized Retry button. */
  onRetry?: () => void;
  ref?: Ref<HTMLDivElement>;
}

export const StyledErrorState = styled.div(({ theme }) => {
  return css`
    height: 100%;

    ${StyledIcon} {
      font-size: 1.5rem;
      height: 1.5rem;
      width: 1.5rem;
      color: ${theme.base.palette.urgent};
    }
  `;
});

StyledErrorState.defaultProps = defaultThemeProp;

const ErrorState: ForwardRefForwardPropsComponent<ErrorStateProps> = forwardRef(function ErrorState(
  props: PropsWithoutRef<ErrorStateProps>,
  ref: ErrorStateProps['ref']
) {
  const t = useI18n();
  const { message = t('unknown_error'), onRetry, ...restProps } = props;

  return (
    <Flex
      {...restProps}
      as={StyledErrorState}
      ref={ref}
      container={{ direction: 'column', alignItems: 'center', justify: 'center', gap: 1 }}
    >
      <Icon name='warn' />
      <Text variant='secondary'>{message}</Text>
      {onRetry && (
        <Button variant='secondary' onClick={onRetry}>
          {t('try_again')}
        </Button>
      )}
    </Flex>
  );
});

export default ErrorState;
